import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { refreshQueries } from '@shared/api/hooks/refresh-queries';
import {
  getCompanyReportsActivitiesStationaryCombustionListQueryKey,
  getCompanyReportsRetrieveQueryKey,
  useCompanyReportsActivitiesDestroy,
  useCompanyReportsActivitiesStationaryCombustionCreate,
  useCompanyReportsActivitiesStationaryCombustionList,
  useCompanyReportsActivitiesStationaryCombustionPreviewRetrieve,
  useCompanyReportsActivitiesStationaryCombustionRetrieve,
  useCompanyReportsActivitiesStationaryCombustionUpdate,
} from '@shared/api/lib/company-reports/company-reports';
import {
  CompanyReportsActivitiesStationaryCombustionListParams,
  CompanyReportsActivitiesStationaryCombustionPreviewRetrieveParams,
  PaginatedStationaryCombustionActivitiesListList,
  StationaryCombustionActivitiesList,
  StationaryCombustionActivityRetrieve,
} from '@shared/api/types';

import { useQueryClient } from '@tanstack/react-query';

export const useGetStationaryCombustionActivityList = (
  companyReportUuid: string,
  params?: CompanyReportsActivitiesStationaryCombustionListParams
) => {
  return useCompanyReportsActivitiesStationaryCombustionList<PaginatedStationaryCombustionActivitiesListList>(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
        cacheTime: 1000 * 60 * 60 * 24,
      },
    }
  );
};

export const useCreateStationaryCombustionActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesStationaryCombustionCreate<StationaryCombustionActivitiesList>(
    {
      request: useAxiosConfig(),
      mutation: {
        onSuccess: (data, variables) => {
          void refreshQueries(
            queryClient,
            getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
          );
          return refreshQueries(
            queryClient,
            getCompanyReportsActivitiesStationaryCombustionListQueryKey(
              variables.companyReportUuid
            )
          );
        },
      },
    }
  );
};

export const useGetStationaryCombustionActivity = ({
  stationaryCombustionActivityUuid,
  reportUuid,
}: {
  stationaryCombustionActivityUuid?: string;
  reportUuid: string;
}) => {
  return useCompanyReportsActivitiesStationaryCombustionRetrieve<StationaryCombustionActivityRetrieve>(
    reportUuid,
    stationaryCombustionActivityUuid!,
    {
      request: useAxiosConfig(),
      query: {
        enabled: !!stationaryCombustionActivityUuid,
      },
    }
  );
};

export const useUpdateStationaryCombustionActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesStationaryCombustionUpdate<StationaryCombustionActivitiesList>(
    {
      request: useAxiosConfig(),
      mutation: {
        onSuccess: (data, variables) => {
          void refreshQueries(
            queryClient,
            getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
          );
          return refreshQueries(
            queryClient,
            getCompanyReportsActivitiesStationaryCombustionListQueryKey(
              variables.companyReportUuid
            )
          );
        },
      },
    }
  );
};

export const useDeleteStationaryCombustionActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (data, variables) => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesStationaryCombustionListQueryKey(
            variables.companyReportUuid
          )
        );
      },
    },
  });
};

export const useGetStationaryCombustionPreview = ({
  companyReportUuid,
  params,
  enabled = true,
}: {
  companyReportUuid: string;
  params?: CompanyReportsActivitiesStationaryCombustionPreviewRetrieveParams;
  enabled?: boolean;
}) => {
  return useCompanyReportsActivitiesStationaryCombustionPreviewRetrieve(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        enabled: enabled,
        cacheTime: 0,
        staleTime: 0,
      },
    }
  );
};
